import { cn } from "@/lib/utils";
import { Fragment } from "react";
import { parseSearchMatch } from "./utils";

interface ArticleMentionTextProps {
  snippet?: string;
  term?: string;
  searchMatch?: string | null;
  className?: string;
}

/** Finds the earliest occurrence of any term; if tie, uses longest match. */
const findNextTerm = (
  text: string,
  terms: string[],
): { index: number; length: number; value: string } | null => {
  let best: { index: number; length: number; value: string } | null = null;
  for (const t of terms) {
    if (!t) continue;
    const idx = text.indexOf(t);
    if (idx === -1) continue;
    if (
      best === null ||
      idx < best.index ||
      (idx === best.index && t.length > best.length)
    ) {
      best = { index: idx, length: t.length, value: t };
    }
  }
  return best;
};

/** Splits text into segments, with every term highlighted. */
const buildHighlightSegments = (
  text: string,
  terms: string[],
): Array<{ type: "text" | "highlight"; value: string }> => {
  if (terms.length === 0) return [{ type: "text", value: text }];
  const segments: Array<{ type: "text" | "highlight"; value: string }> = [];
  let remaining = text;
  while (remaining.length > 0) {
    const found = findNextTerm(remaining, terms);
    if (found === null) {
      segments.push({ type: "text", value: remaining });
      break;
    }
    if (found.index > 0) {
      segments.push({ type: "text", value: remaining.slice(0, found.index) });
    }
    segments.push({ type: "highlight", value: found.value });
    remaining = remaining.slice(found.index + found.length);
  }
  return segments;
};

const ArticleMentionText = ({
  snippet,
  term,
  searchMatch,
  className = "",
}: ArticleMentionTextProps) => {
  const parsedSearch = searchMatch ? parseSearchMatch(searchMatch) : null;
  const finalSnippet = parsedSearch?.mention ?? snippet ?? "";
  const finalTerm = parsedSearch?.term ?? term ?? "";
  const finalTerms =
    parsedSearch?.terms && parsedSearch.terms.length > 0
      ? parsedSearch.terms
      : finalTerm
        ? [finalTerm]
        : [];

  const getProcessedSnippet = (text: string, searchTerm: string) => {
    if (!className.includes("clamp")) {
      return text;
    }
    const termIndex = text.toLowerCase().indexOf(searchTerm.toLowerCase());
    if (termIndex === -1) return text;
    if (termIndex < 100) return text;
    const start = Math.max(0, termIndex - 100);
    return `...${text.slice(start)}`;
  };

  const processedSnippet = getProcessedSnippet(finalSnippet, finalTerm);
  const segments = buildHighlightSegments(processedSnippet, finalTerms);

  return (
    <p className={cn("text-xs tracking-wide text-slate-500", className)}>
      "...
      {segments.map((seg, index) =>
        seg.type === "highlight" ? (
          <span
            key={`highlight-${index}-${seg.value}`}
            className="bg-yellow-100 p-0.5 -mx-0.5 text-gray-500"
          >
            {seg.value}
          </span>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: segments are in stable order; key needed for React list
          <Fragment key={`text-${index}`}>{seg.value}</Fragment>
        ),
      )}
      ..."
    </p>
  );
};

export default ArticleMentionText;
